/**
 * Cache Configuration
 * TTL (Time To Live) values for different data types
 *
 * Strategy: Stale-While-Revalidate with differentiated TTLs
 */

export const CACHE_CONFIG = {
  // Very static data (changes rarely)
  VERY_STATIC: {
    TTL: 60 * 60 * 1000, // 1 hour
    keys: ['levels', 'subjects', 'workspace'],
  },

  // Semi-static data (changes occasionally)
  SEMI_STATIC: {
    TTL: 30 * 60 * 1000, // 30 minutes
    keys: ['courses', 'teachers', 'students'],
  },

  // Academic cycles (need faster refresh)
  ACADEMIC_CYCLES: {
    TTL: 10 * 60 * 1000, // 10 minutes
    keys: ['academicCycles'],
  },

  // Dynamic data (changes frequently)
  DYNAMIC: {
    TTL: 5 * 60 * 1000, // 5 minutes
    keys: ['enrollments', 'userProfile', 'dashboardStats'],
  },

  // Very dynamic data (needs to be fresh)
  VERY_DYNAMIC: {
    TTL: 1 * 60 * 1000, // 1 minute
    keys: ['notifications', 'evaluationsPending'],
  },

  // Real-time data
  REAL_TIME: {
    TTL: 30 * 1000, // 30 seconds
    keys: ['submissions', 'liveStats'],
  },
} as const;

/**
 * Get TTL for a specific cache key
 */
export function getTTL(key: string): number {
  for (const config of Object.values(CACHE_CONFIG)) {
    if ((config.keys as readonly string[]).includes(key)) {
      return config.TTL;
    }
  }
  // Default TTL if key not found
  return CACHE_CONFIG.DYNAMIC.TTL;
}

/**
 * Check if cached data is stale
 */
export function isStale(lastFetched: number | null, ttl: number): boolean {
  if (!lastFetched) return true;
  return Date.now() - lastFetched > ttl;
}

/**
 * Polling intervals for different data types
 */
export const POLLING_INTERVALS = {
  NOTIFICATIONS: 60 * 1000, // 1 minute
  EVALUATIONS: 2 * 60 * 1000, // 2 minutes
  STATS: 5 * 60 * 1000, // 5 minutes
} as const;

/**
 * Refetch strategies
 */
export const REFETCH_STRATEGIES = {
  ON_FOCUS: true, // Refetch when window regains focus
  ON_RECONNECT: true, // Refetch when internet reconnects
  ON_MOUNT: false, // Don't refetch on component mount (use cache)
} as const;
